"use client";
/**
 * Kalender / Calendar — Svendetid marketing feature page.
 *
 * Built to mirror the reports feature page (reports-content.tsx) on the
 * shared svendetid-ds design system: a live dashboard mockup in the hero, a
 * "without / with" comparison, three action-oriented feature blocks with mini
 * mockups, a dark AI block, a feature grid, a platform grid and a FAQ.
 *
 * Language-parametrized so /features/calendar (da) and /en/features/calendar
 * (en) share one source.
 */
import { useState } from "react";
import Link from "next/link";
import { SignUpButton } from "@clerk/nextjs";
import {
  dsFontClass, dsTokensCss, Icon, Footer, type Lang,
} from "./svendetid-ds";
import LandingNav from "./landing-nav";

const T = {
  da: {
    eyebrow: "Kalender i Svendetid",
    h1a: "En kalender der holder ",
    h1b: "hele sjakket",
    h1c: " synkroniseret",
    sub: "Planlæg jobs, besøg og folk på ét sted — og se hvem der er hvor, lige nu. Ændringer rammer web og mobil med det samme, så ingen møder op til det forkerte.",
    cta: "Kom i gang – det er gratis",
    seePricing: "Se priser",

    trust2: "Gratis at komme i gang",
    trust3: "Dansk support",
    // dashboard mockup
    mockReport: "Kalender",
    mockTitle: "Ugeoverblik",
    mockPeriod: "Denne uge",
    mockShare: "Del",
    kpis: [
      { label: "Planlagte jobs", value: "27", note: "▲ 5 vs. sidste uge", noteColor: "var(--brand)" },
      { label: "Kapacitet brugt", value: "82 %", note: "▲ 6 pct.point", noteColor: "var(--brand)" },
      { label: "Besøg i dag", value: "9", note: "3 sjak ude", noteColor: "var(--text-muted)" },
      { label: "Uplanlagt", value: "4", note: "mangler ansvarlig", noteColor: "var(--accent-strong)", valueColor: "var(--danger)" },
    ],
    chartTitle: "Timer per dag",
    chartUnit: "t · 7 dage",
    donutTitle: "Jobs per sjak",
    donutCenter: "jobs",
    statuses: [
      { label: "Sjak Nord", count: "10", color: "#2056C0" },
      { label: "Sjak Syd", count: "4", color: "#A57423" },
      { label: "Service", count: "5", color: "var(--accent)" },
      { label: "Ledig", count: "5", color: "var(--brand)" },
    ],
    recentTitle: "I dag",
    seeAll: "Se alle",
    rows: [
      { initial: "N", bg: "var(--tint-peach)", color: "var(--accent-ink)", title: "Tagrenovering", sub: "Nordby Bolig · 08:00", status: "I gang", statusBg: "var(--tint-mint)", statusColor: "var(--brand)", amount: "Martin K." },
      { initial: "K", bg: "var(--tint-lilac)", color: "#4B3F8F", title: "Varmepumpe-service", sub: "Fam. Kruse · 11:30", status: "Planlagt", statusBg: "var(--orange-100)", statusColor: "var(--accent-strong)", amount: "Sjak Syd" },
      { initial: "L", bg: "var(--tint-rose)", color: "var(--danger)", title: "Badrenovering", sub: "Fam. Lund · 13:00", status: "Forsinket", statusBg: "var(--tint-rose)", statusColor: "var(--danger)", amount: "Uplanlagt" },
    ],
    // without / with
    cmpH2: "En plan alle kan stole på — ikke gætværk",
    withoutLabel: "Uden Svendetid",
    withLabel: "Med Svendetid",
    without: [
      "Planen ligger i hovedet på én person",
      "Folk møder op til det forkerte job eller den forkerte adresse",
      "Dobbeltbookinger opdages først når sjakket står og venter",
      "Ringerunder hver morgen for at høre hvem der gør hvad",
    ],
    with: [
      "Hele sjakket ser den samme plan — altid opdateret",
      "Job, adresse og ansvarlig følges ad, hele vejen til mobilen",
      "Konflikter og overbookninger fanges før de bliver et problem",
      "Tildel og flyt jobs med træk-og-slip — alle får besked",
    ],
    // section intro
    introLabel: "Sådan virker det",
    introH2: "Planlægning der virker som dit sjak gør",
    introSub: "Visuel. Fleksibel. Altid i sync.",
    // three feature blocks
    blocks: [
      {
        label: "Træk og slip",
        h3: "Flyt et job. Tildel et sjak. Samme skærm.",
        body: "Træk jobs hen på den rette dag og det rette sjak direkte i kalenderen. Når noget skrider, flytter du det med det samme — ingen nye faner, ingen telefonopkald. Planen er altid i sync, fordi den er arbejdet.",
        chips: [
          { icon: "swap-horizontal-outline", text: "Flyt job" },
          { icon: "people-outline", text: "Tildel sjak" },
          { icon: "time-outline", text: "Skift tidspunkt" },
        ],
        mock: "edit",
      },
      {
        label: "Fleksibel",
        h3: "Dag, uge eller måned — og ét sjak ad gangen",
        body: "Skift mellem dags-, uge- og månedsvisning, eller filtrér ned til ét enkelt sjak. Se belastningen på tværs af holdet, find det ledige hul og book besøget — præcis det view, hver person har brug for.",
        chips: [
          { icon: "calendar-outline", text: "Uge & måned" },
          { icon: "people-outline", text: "Per sjak" },
          { icon: "repeat-outline", text: "Gentagelser" },
        ],
        mock: "flexible",
      },
      {
        label: "Mobil",
        h3: "Dagens plan i lommen — også offline",
        body: "Hele sjakket har dagens jobs, adresser og kontakter på telefonen. Naviger til adressen, start tidsregistrering fra begivenheden og få push når noget flytter sig. Virker også uden dækning på pladsen.",
        chips: [
          { icon: "phone-portrait-outline", text: "Mobil" },
          { icon: "navigate-outline", text: "Navigation" },
          { icon: "notifications-outline", text: "Push" },
        ],
        mock: "share",
      },
    ],
    // mock labels
    mockJobLabel: "Job · Tagrenovering",
    mockStatus: "Dag",
    mockResponsible: "Sjak",
    mockAmount: "Tid",
    mockSaved: "Gemt",
    mockFrom: "Man",
    mockTo: "Tir",
    mockHoursTitle: "Belastning per dag · denne uge",
    mockMaterials: "Besøg",
    mockMargin: "Ledig tid",
    sharePortal: "Dagens plan",
    shareLive: "Live",
    shareCustomer: "Anders (sjak)",
    shareCustomerRole: "08:00 Nordby",
    shareBookkeeper: "Mette (sjak)",
    shareBookkeeperRole: "11:30 Kruse",
    shareHidden: "Ledig fra 14:00",
    shareHiddenRole: "Ledig",
    // AI block
    aiLabel: "AI-drevet planlægning",
    aiH2a: "Svendetid AI lægger ",
    aiH2b: "den bedste plan",
    aiBody: "Spørg om hvad som helst: »Hvem kan tage et akut besøg i Aarhus i morgen?« eller »Hvor har vi ledig kapacitet næste uge?«. AI'en kender sjakkenes plan, køretid og kompetencer og foreslår den rute der spilder mindst tid.",
    aiCta: "Prøv Svend AI",
    aiQuestion: "Hvem kan tage et akut besøg i Aarhus i morgen formiddag?",
    aiAnswerIntro: "2 sjak har plads i morgen formiddag:",
    aiRow1: "Sjak Nord · 12 min væk",
    aiRow1Val: "kl 09:30",
    aiRow2: "Sjak Syd · 24 min væk",
    aiRow2Val: "kl 10:15",
    aiAnswerOutro: "Sjak Nord ligger tættest og har et hul kl 09:30. Vil du booke?",
    // feature grid
    gridH2: "Plus alt det, du skal bruge for at planlægge dagen",
    grid: [
      { icon: "calendar-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Uge- & månedsoverblik", body: "Se alle jobs, besøg og folk i ét samlet kalendervindue — og skift mellem dag, uge og måned med et klik." },
      { icon: "people-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Teamplanlægning", body: "Tildel og planlæg jobs direkte fra kalenderen. Se med det samme hvem der er ledig, og hvem der er overbooket." },
      { icon: "repeat-outline", iconBg: "var(--info-soft)", iconColor: "#2056C0", title: "Tilbagevendende opgaver", body: "Planlæg vedligehold og servicebesøg automatisk med gentagende begivenheder. Sæt det op én gang." },
      { icon: "location-outline", iconBg: "var(--tint-lilac)", iconColor: "#4B3F8F", title: "Lokationsbaseret oversigt", body: "Knyt besøg og jobs til adresser, se dem i kontekst og naviger direkte fra kalenderen." },
      { icon: "time-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Tidsregistrering integreret", body: "Start tidsregistrering direkte fra en kalenderbegivenhed med ét klik. Timer lander automatisk på jobbet." },
      { icon: "pulse-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Realtidsopdateringer", body: "Ændringer synkroniseres øjeblikkeligt på tværs af web og mobil. Hele sjakket ser den samme plan." },
    ],
    // platform
    platLabel: "Svendetid-platformen",
    platH2: "Kalenderen er kun begyndelsen",
    platSub: "Kalenderen er en del af platformen — jobs, tilbud, tid, kunder og fakturering i ét samlet system. Alt taler sammen.",
    platform: [
      { icon: "briefcase-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Jobs", sub: "Styr hvert job", href: "/features" },
      { icon: "receipt-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Tilbud", sub: "Vind flere opgaver", href: "/features/offer" },
      { icon: "bar-chart-outline", iconBg: "var(--info-soft)", iconColor: "#2056C0", title: "Rapporter", sub: "Overblik der virker", href: "/features/reports" },
      { icon: "time-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Tid", sub: "Registrér timer", href: "/features/time-registration" },
      { icon: "people-outline", iconBg: "var(--tint-peach)", iconColor: "var(--accent-ink)", title: "Kunder", sub: "Hele historikken", href: "/features/customer-database" },
      { icon: "layers-outline", iconBg: "var(--sage-100)", iconColor: "var(--text-muted)", title: "Alle funktioner", sub: "Se hele platformen", href: "/features" },
    ],
    // security
    secLabel: "Sikkerhed hele vejen",
    security: [
      { icon: "shield-checkmark-outline", text: "GDPR" },
      { icon: "cloud-outline", text: "Data i EU" },
      { icon: "chatbubbles-outline", text: "Support på dansk" },
    ],
    // FAQ
    faqH2: "Ofte stillede spørgsmål",
    faqs: [
      { q: "Synkroniserer kalenderen med web og mobil?", a: "Ja. Hver ændring rammer både web og mobilappen i realtid. Når du flytter et job eller tildeler et sjak, ser hele holdet den nye plan med det samme — uden at opdatere noget manuelt." },
      { q: "Kan jeg planlægge tilbagevendende besøg?", a: "Ja. Opret gentagende begivenheder til service- og vedligeholdelsesbesøg — dagligt, ugentligt, månedligt eller efter eget mønster. Sæt det op én gang, så holder kalenderen styr på resten." },
      { q: "Virker mobilappen offline?", a: "Dagens planlagte begivenheder er tilgængelige offline, så sjakket kan se jobs og adresser uden dækning på pladsen. Ændringer synkroniseres, så snart der er forbindelse igen." },
      { q: "Kan jeg se kun ét sjak ad gangen?", a: "Ja. Filtrér kalenderen ned til ét enkelt sjak, en person eller en jobtype. Byg præcis det view, hver person har brug for — fra det fulde overblik til den enkelte rute." },
     ],
    // CTA band
    bandH2: "Hold hele sjakket på samme plan",
    bandBody: "Slut dig til håndværkere der planlægger og koordinerer fra samme sted. Intet betalingskort. Gratis for altid.",
  },
  en: {
    eyebrow: "Calendar in Svendetid",
    h1a: "A calendar that keeps the ",
    h1b: "whole crew",
    h1c: " in sync",
    sub: "Plan jobs, visits and people in one place — and see who's where, right now. Changes hit web and mobile instantly, so nobody shows up to the wrong place.",
    cta: "Get started – it's free",
    seePricing: "See pricing",

    trust2: "Free to get started",
    trust3: "Danish support",
    mockReport: "Calendar",
    mockTitle: "Week overview",
    mockPeriod: "This week",
    mockShare: "Share",
    kpis: [
      { label: "Scheduled jobs", value: "27", note: "▲ 5 vs. last week", noteColor: "var(--brand)" },
      { label: "Capacity used", value: "82 %", note: "▲ 6 pct. points", noteColor: "var(--brand)" },
      { label: "Visits today", value: "9", note: "3 crews out", noteColor: "var(--text-muted)" },
      { label: "Unscheduled", value: "4", note: "missing owner", noteColor: "var(--accent-strong)", valueColor: "var(--danger)" },
    ],
    chartTitle: "Hours per day",
    chartUnit: "h · 7 days",
    donutTitle: "Jobs per crew",
    donutCenter: "jobs",
    statuses: [
      { label: "Crew North", count: "10", color: "#2056C0" },
      { label: "Crew South", count: "4", color: "#A57423" },
      { label: "Service", count: "5", color: "var(--accent)" },
      { label: "Free", count: "5", color: "var(--brand)" },
    ],
    recentTitle: "Today",
    seeAll: "See all",
    rows: [
      { initial: "N", bg: "var(--tint-peach)", color: "var(--accent-ink)", title: "Roof renovation", sub: "Nordby Bolig · 08:00", status: "In progress", statusBg: "var(--tint-mint)", statusColor: "var(--brand)", amount: "Martin K." },
      { initial: "K", bg: "var(--tint-lilac)", color: "#4B3F8F", title: "Heat pump service", sub: "Kruse family · 11:30", status: "Scheduled", statusBg: "var(--orange-100)", statusColor: "var(--accent-strong)", amount: "Crew South" },
      { initial: "L", bg: "var(--tint-rose)", color: "var(--danger)", title: "Bathroom renovation", sub: "Lund family · 13:00", status: "Delayed", statusBg: "var(--tint-rose)", statusColor: "var(--danger)", amount: "Unscheduled" },
    ],
    cmpH2: "A plan everyone can trust — not guesswork",
    withoutLabel: "Without Svendetid",
    withLabel: "With Svendetid",
    without: [
      "The plan lives in one person's head",
      "People show up to the wrong job or the wrong address",
      "Double-bookings surface only when the crew is standing around waiting",
      "Morning phone rounds just to hear who's doing what",
    ],
    with: [
      "The whole crew sees the same plan — always up to date",
      "Job, address and owner travel together, all the way to mobile",
      "Conflicts and over-bookings get caught before they're a problem",
      "Assign and move jobs with drag-and-drop — everyone gets notified",
    ],
    introLabel: "How it works",
    introH2: "Scheduling that works the way your crew does",
    introSub: "Visual. Flexible. Always in sync.",
    blocks: [
      {
        label: "Drag and drop",
        h3: "Move a job. Assign a crew. Same screen.",
        body: "Drag jobs onto the right day and the right crew straight in the calendar. When something slips, you move it right away — no new tabs, no phone calls. The plan is always in sync, because it is the work.",
        chips: [
          { icon: "swap-horizontal-outline", text: "Move job" },
          { icon: "people-outline", text: "Assign crew" },
          { icon: "time-outline", text: "Change time" },
        ],
        mock: "edit",
      },
      {
        label: "Flexible",
        h3: "Day, week or month — and one crew at a time",
        body: "Switch between day, week and month views, or filter down to a single crew. See workload across the team, find the open slot and book the visit — exactly the view each person needs.",
        chips: [
          { icon: "calendar-outline", text: "Week & month" },
          { icon: "people-outline", text: "Per crew" },
          { icon: "repeat-outline", text: "Recurring" },
        ],
        mock: "flexible",
      },
      {
        label: "Mobile",
        h3: "The day's plan in your pocket — even offline",
        body: "The whole crew has today's jobs, addresses and contacts on their phone. Navigate to the address, start time tracking from the event and get a push when something moves. Works even with no signal on site.",
        chips: [
          { icon: "phone-portrait-outline", text: "Mobile" },
          { icon: "navigate-outline", text: "Navigation" },
          { icon: "notifications-outline", text: "Push" },
        ],
        mock: "share",
      },
    ],
    mockJobLabel: "Job · Roof renovation",
    mockStatus: "Day",
    mockResponsible: "Crew",
    mockAmount: "Time",
    mockSaved: "Saved",
    mockFrom: "Mon",
    mockTo: "Tue",
    mockHoursTitle: "Workload per day · this week",
    mockMaterials: "Visits",
    mockMargin: "Free time",
    sharePortal: "Today's plan",
    shareLive: "Live",
    shareCustomer: "Anders (crew)",
    shareCustomerRole: "08:00 Nordby",
    shareBookkeeper: "Mette (crew)",
    shareBookkeeperRole: "11:30 Kruse",
    shareHidden: "Free from 14:00",
    shareHiddenRole: "Free",
    aiLabel: "AI-driven scheduling",
    aiH2a: "Svendetid AI builds ",
    aiH2b: "the best plan",
    aiBody: "Ask anything: “Who can take an urgent visit in Aarhus tomorrow?” or “Where do we have spare capacity next week?”. The AI knows each crew's plan, travel time and skills, and suggests the route that wastes the least time.",
    aiCta: "Try Svendetid AI",
    aiQuestion: "Who can take an urgent visit in Aarhus tomorrow morning?",
    aiAnswerIntro: "2 crews have room tomorrow morning:",
    aiRow1: "Crew North · 12 min away",
    aiRow1Val: "09:30",
    aiRow2: "Crew South · 24 min away",
    aiRow2Val: "10:15",
    aiAnswerOutro: "Crew North is closest and has a gap at 09:30. Want to book it?",
    gridH2: "Plus everything you need to plan the day",
    grid: [
      { icon: "calendar-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Week & month overview", body: "See all jobs, visits and people in one unified calendar view — and switch between day, week and month with one click." },
      { icon: "people-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Team scheduling", body: "Assign and plan jobs directly from the calendar. Instantly see who's free and who's over-booked." },
      { icon: "repeat-outline", iconBg: "var(--info-soft)", iconColor: "#2056C0", title: "Recurring tasks", body: "Schedule maintenance and service visits automatically with repeating events. Set it up once." },
      { icon: "location-outline", iconBg: "var(--tint-lilac)", iconColor: "#4B3F8F", title: "Location-based overview", body: "Link visits and jobs to addresses, view them in context and navigate straight from the calendar." },
      { icon: "time-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Time tracking integrated", body: "Start time tracking directly from a calendar event with one click. Hours land on the job automatically." },
      { icon: "pulse-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Real-time updates", body: "Changes sync instantly across web and mobile. The whole crew sees the same plan." },
    ],
    platLabel: "The Svendetid platform",
    platH2: "The calendar is only the beginning",
    platSub: "The calendar is part of the platform — jobs, quotes, time, customers and invoicing in one connected system. Everything talks to everything.",
    platform: [
      { icon: "briefcase-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Jobs", sub: "Run every job", href: "/en/features" },
      { icon: "receipt-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Quotes", sub: "Win more work", href: "/features/offer" },
      { icon: "bar-chart-outline", iconBg: "var(--info-soft)", iconColor: "#2056C0", title: "Reports", sub: "Overview that works", href: "/en/features/reports" },
      { icon: "time-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Time", sub: "Register hours", href: "/en/features/time-registration" },
      { icon: "people-outline", iconBg: "var(--tint-peach)", iconColor: "var(--accent-ink)", title: "Customers", sub: "The whole history", href: "/en/features/customer-database" },
      { icon: "layers-outline", iconBg: "var(--sage-100)", iconColor: "var(--text-muted)", title: "All features", sub: "See the whole platform", href: "/en/features" },
    ],
    secLabel: "Security all the way",
    security: [
      { icon: "shield-checkmark-outline", text: "GDPR" },
      { icon: "cloud-outline", text: "Data in the EU" },
      { icon: "chatbubbles-outline", text: "Support in Danish" },
    ],
    faqH2: "Frequently asked questions",
    faqs: [
      { q: "Does the calendar sync across web and mobile?", a: "Yes. Every change hits both web and the mobile app in real time. When you move a job or assign a crew, the whole team sees the new plan instantly — without refreshing anything manually." },
      { q: "Can I schedule recurring visits?", a: "Yes. Create repeating events for service and maintenance visits — daily, weekly, monthly or on your own pattern. Set it up once and the calendar keeps track of the rest." },
      { q: "Does the mobile app work offline?", a: "Today's scheduled events are available offline, so the crew can see jobs and addresses with no signal on site. Changes sync as soon as there's a connection again." },
      { q: "Can I view just one crew at a time?", a: "Yes. Filter the calendar down to a single crew, person or job type. Build exactly the view each person needs — from the full overview to one individual route." },
         ],
    bandH2: "Keep the whole crew on the same plan",
    bandBody: "Join trades businesses that plan and coordinate from the same place. No payment card. Free forever.",
  },
} as const;

type TT = (typeof T)["da"] | (typeof T)["en"];

const cardStyle: React.CSSProperties = {
  background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18,
  padding: 28, boxShadow: "0 6px 16px rgba(16,33,27,0.06)", display: "flex", flexDirection: "column", gap: 15,
};

const monoLabel: React.CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em",
  textTransform: "uppercase", color: "var(--accent-strong)",
};

function SectionEyebrow({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 14 }}>
      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
      <span style={monoLabel}>{label}</span>
      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
    </div>
  );
}

type Card = { icon: string; iconBg: string; iconColor: string; title: string; body: string };

function FeatureCard({ icon, iconBg, iconColor, title, body }: Card) {
  return (
    <div style={cardStyle}>
      <div style={{ width: 48, height: 48, borderRadius: 14, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon name={icon} size={24} color={iconColor} />
      </div>
      <h3 style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 21, letterSpacing: "-0.01em", margin: 0, color: "var(--green-900)" }}>{title}</h3>
      <p style={{ fontSize: 15, lineHeight: 1.55, color: "var(--text-muted)", margin: 0 }}>{body}</p>
    </div>
  );
}

/* --------------------------- Feature-block mocks ------------------------- */
function EditMock({ t }: { t: TT }) {
  return (
    <div style={{ width: "100%", background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18, boxShadow: "0 20px 44px rgba(16,33,27,0.16)", padding: 18 }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 12 }}>{t.mockJobLabel}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid var(--hairline)" }}>
        <span style={{ fontSize: 13, color: "var(--text-muted)", width: 64 }}>{t.mockStatus}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#2056C0", background: "var(--info-soft)", borderRadius: 8, padding: "5px 10px" }}>{t.mockFrom}</span>
        <Icon name="arrow-forward" size={14} color="var(--text-faint)" />
        <span style={{ fontSize: 12, fontWeight: 600, color: "#fff", background: "var(--accent)", borderRadius: 8, padding: "5px 10px", boxShadow: "0 6px 16px rgba(194,65,12,0.26)" }}>{t.mockTo}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid var(--hairline)" }}>
        <span style={{ fontSize: 13, color: "var(--text-muted)", width: 64 }}>{t.mockResponsible}</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 600 }}>
          <span style={{ width: 24, height: 24, borderRadius: "50%", background: "var(--tint-mint)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-serif)", fontSize: 12, color: "var(--brand)" }}>M</span>Martin K.
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0" }}>
        <span style={{ fontSize: 13, color: "var(--text-muted)", width: 64 }}>{t.mockAmount}</span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 15, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>08:00–12:00</span>
        <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 600, color: "var(--brand)", display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="checkmark-circle" size={14} color="var(--brand)" />{t.mockSaved}</span>
      </div>
    </div>
  );
}

function FlexibleMock({ t }: { t: TT }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      <div style={{ gridColumn: "1 / -1", background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 16, boxShadow: "0 12px 28px rgba(16,33,27,0.1)", padding: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--green-900)", marginBottom: 10 }}>{t.mockHoursTitle}</div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 64 }}>
          {["60%", "85%", "45%", "72%", "96%"].map((h, i) => (
            <div key={i} style={{ flex: 1, height: h, background: i === 1 ? "var(--brand)" : i === 4 ? "var(--accent)" : "var(--green-100)", borderRadius: "5px 5px 0 0" }} />
          ))}
        </div>
      </div>
      <div style={{ background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 16, boxShadow: "0 12px 28px rgba(16,33,27,0.1)", padding: 16 }}>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)" }}>{t.mockMaterials}</div>
        <div style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 22, marginTop: 6 }}>9</div>
      </div>
      <div style={{ background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 16, boxShadow: "0 12px 28px rgba(16,33,27,0.1)", padding: 16 }}>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)" }}>{t.mockMargin}</div>
        <div style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 22, marginTop: 6, color: "var(--brand)" }}>3 t</div>
      </div>
    </div>
  );
}

function ShareMock({ t }: { t: TT }) {
  return (
    <div style={{ width: "100%", background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18, boxShadow: "0 20px 44px rgba(16,33,27,0.16)", padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 14, borderBottom: "1px solid var(--hairline)" }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--orange-100)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="phone-portrait-outline" size={18} color="var(--accent-strong)" /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{t.sharePortal}</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.mockPeriod}</div>
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--brand)", background: "var(--tint-mint)", borderRadius: 8, padding: "4px 9px" }}>{t.shareLive}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--tint-teal)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-serif)", fontSize: 12, color: "#0E5E54" }}>A</span>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{t.shareCustomer}</div>
          <span style={{ fontSize: 11, color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="time-outline" size={14} />{t.shareCustomerRole}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--tint-lilac)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-serif)", fontSize: 12, color: "#4B3F8F" }}>M</span>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{t.shareBookkeeper}</div>
          <span style={{ fontSize: 11, color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="time-outline" size={14} />{t.shareBookkeeperRole}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, opacity: 0.6 }}>
          <span style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--sage-100)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="checkmark-circle" size={13} color="var(--brand)" /></span>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "var(--text-muted)" }}>{t.shareHidden}</div>
          <span style={{ fontSize: 11, color: "var(--brand)" }}>{t.shareHiddenRole}</span>
        </div>
      </div>
    </div>
  );
}

const MOCK_BG: Record<string, string> = {
  edit: "linear-gradient(155deg,var(--orange-100),#FFF7EF)",
  flexible: "linear-gradient(155deg,var(--green-50),#EEF6F0)",
  share: "linear-gradient(155deg,var(--tint-lilac),#F4F2FB)",
};

function FeatureBlock({ block, reverse, t }: { block: TT["blocks"][number]; reverse: boolean; t: TT }) {
  const text = (
    <div style={{ order: reverse ? 2 : 1 }}>
      <div style={{ ...monoLabel, marginBottom: 14 }}>{block.label}</div>
      <h3 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 32, lineHeight: 1.12, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{block.h3}</h3>
      <p style={{ fontSize: 17, color: "var(--text-muted)", lineHeight: 1.6, margin: "18px 0 0" }}>{block.body}</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 22 }}>
        {block.chips.map((c) => (
          <span key={c.text} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 500, background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 999, padding: "7px 13px" }}>
            <Icon name={c.icon} size={15} color="var(--brand)" />{c.text}
          </span>
        ))}
      </div>
    </div>
  );
  const visual = (
    <div style={{ order: reverse ? 1 : 2, background: MOCK_BG[block.mock], borderRadius: 28, padding: "clamp(28px,5%,48px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {block.mock === "edit" && <EditMock t={t} />}
      {block.mock === "flexible" && <FlexibleMock t={t} />}
      {block.mock === "share" && <ShareMock t={t} />}
    </div>
  );
  return (
    <section style={{ maxWidth: 1180, margin: "0 auto", padding: "48px 32px" }}>
      <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center" }}>
        {text}
        {visual}
      </div>
    </section>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>
      <button onClick={() => setOpen((o) => !o)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 16, padding: "20px 22px", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontFamily: "var(--font-sans)" }}>
        <span style={{ flex: 1, fontSize: 16, fontWeight: 600, color: "var(--green-900)" }}>{q}</span>
        <svg viewBox="0 0 24 24" width={18} height={18} fill="none" style={{ color: "var(--accent-strong)", flex: "none", transition: "transform .25s ease", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && <div style={{ padding: "0 22px 22px", fontSize: 15, lineHeight: 1.6, color: "var(--text-muted)" }}>{a}</div>}
    </div>
  );
}

export default function CalendarContent({ lang = "da" }: { lang?: Lang }) {
  const t = T[lang];
  const pricingHref = lang === "en" ? "/en/pricing" : "/pricing";
  const aiHref = lang === "en" ? "/en/ai" : "/ai";
  return (
    <div className={`svendetid-ds ${dsFontClass}`} style={{ background: "#FFFFFF" }}>
      <style>{dsTokensCss}</style>
      <style>{`
        html,body{background:#FFFFFF;overscroll-behavior-y:none;overflow-x:hidden;overflow-x:clip;}
        @media (max-width: 720px){
          .svendetid-ds .rep-kpi{grid-template-columns:1fr 1fr !important;}
          .svendetid-ds .rep-charts{grid-template-columns:1fr !important;}
        }
      `}</style>

      <LandingNav lang={lang} accent="blue" />

      {/* ===== HERO ===== */}
      <section style={{ background: "#FFFFFF", color: "var(--text-body)", paddingTop: 64 }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "72px 32px 40px", textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, ...monoLabel, marginBottom: 22 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--accent)" }} />{t.eyebrow}
          </div>
          <h1 className="ds-h1" style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 58, lineHeight: 1.03, letterSpacing: "-0.025em", margin: "0 auto", maxWidth: "14ch", color: "var(--green-900)", textWrap: "balance" }}>
            {t.h1a}<span style={{ fontStyle: "italic", color: "var(--brand)" }}>{t.h1b}</span>{t.h1c}
          </h1>
          <p style={{ fontSize: 19, lineHeight: 1.6, color: "var(--text-muted)", maxWidth: "60ch", margin: "24px auto 0", textWrap: "pretty" }}>{t.sub}</p>
          <div className="ds-cta-row" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginTop: 34 }}>
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-blue" style={{ cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 26px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.cta}<Icon name="arrow-forward" size={18} /></button></SignUpButton>
            <Link href={pricingHref} className="btn-soft" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "var(--green-900)", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, border: "1px solid var(--border)", background: "var(--surface-card)" }}>{t.seePricing}</Link>
          </div>
          <div style={{ display: "flex", gap: 22, justifyContent: "center", alignItems: "center", flexWrap: "wrap", marginTop: 20, fontSize: 13, color: "var(--text-muted)" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="checkmark-circle" size={16} color="var(--brand)" />{t.trust2}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="checkmark-circle" size={16} color="var(--brand)" />{t.trust3}</span>
          </div>

          {/* HERO DASHBOARD IMAGE — Ugeoverblik */}
          <div style={{ marginTop: 54, position: "relative" }}>
            {/* thick border fading from the page background (top) → yellow → dark green (bottom) */}
            <div
              style={{
                position: "relative",
                zIndex: 1,
                padding: 16,
                borderRadius: 14,
                background: "linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 40%, #F0B429 72%, var(--green-900) 100%)",
              }}
            >
              <img
                src="/Calendar.png"
                alt={t.mockTitle}
                style={{ display: "block", width: "100%", height: "auto", borderRadius: 16 }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== WITHOUT / WITH ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "64px 32px" }}>
        <div style={{ textAlign: "center", maxWidth: "60ch", margin: "0 auto 40px" }}>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 40, lineHeight: 1.08, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)", textWrap: "balance" }}>{t.cmpH2}</h2>
        </div>
        <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div style={{ background: "var(--green-50)", border: "1px solid var(--border)", borderRadius: 20, padding: 30 }}>
            <div style={{ ...monoLabel, color: "var(--text-muted)", marginBottom: 18 }}>{t.withoutLabel}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {t.without.map((item) => (
                <div key={item} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <svg viewBox="0 0 24 24" width={22} height={22} style={{ flex: "none", marginTop: 1, background: "var(--tint-rose)", borderRadius: "50%", padding: 3, color: "var(--danger)" }}><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" /></svg>
                  <span style={{ fontSize: 15, color: "var(--text-muted)", lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ background: "var(--green-900)", border: "1px solid #2C3B35", borderRadius: 20, padding: 30, boxShadow: "0 24px 60px rgba(16,33,27,0.22)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, ...monoLabel, color: "var(--accent)", marginBottom: 18 }}>
              <img src="/svendetid-logo.png" alt="" style={{ width: 18, height: 18 }} />{t.withLabel}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {t.with.map((item) => (
                <div key={item} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <svg viewBox="0 0 24 24" width={22} height={22} style={{ flex: "none", marginTop: 1, background: "var(--brand)", borderRadius: "50%", padding: 3, color: "#fff" }}><path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span style={{ fontSize: 15, color: "var(--text-on-hero)", lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION INTRO ===== */}
      <section style={{ maxWidth: 760, margin: "0 auto", padding: "40px 32px 8px", textAlign: "center" }}>
        <SectionEyebrow label={t.introLabel} />
        <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{t.introH2}</h2>
        <p style={{ fontSize: 17, color: "var(--text-muted)", margin: "16px auto 0", maxWidth: "48ch" }}>{t.introSub}</p>
      </section>

      {/* ===== THREE FEATURE BLOCKS ===== */}
      {t.blocks.map((block, i) => (
        <FeatureBlock key={block.label} block={block} reverse={i === 1} t={t} />
      ))}

      {/* ===== AI BLOCK ===== */}
      <section style={{ padding: "64px 32px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", background: "var(--green-900)", borderRadius: 28, padding: "clamp(32px,5vw,64px)", boxShadow: "0 24px 60px rgba(16,33,27,0.22)", position: "relative", overflow: "hidden" }}>
          <div aria-hidden style={{ position: "absolute", top: "-30%", right: "-10%", width: 420, height: 420, background: "radial-gradient(circle, rgba(249,115,22,0.22), rgba(249,115,22,0) 70%)", pointerEvents: "none" }} />
          <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center", position: "relative" }}>
            <div>
              <div style={{ ...monoLabel, color: "var(--accent)", marginBottom: 16 }}>{t.aiLabel}</div>
              <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 40, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--text-on-hero)" }}>{t.aiH2a}<span style={{ fontStyle: "italic", color: "#F4C8A6" }}>{t.aiH2b}</span></h2>
              <p style={{ fontSize: 17, lineHeight: 1.6, color: "var(--text-hero-muted)", margin: "20px 0 0", maxWidth: "46ch" }}>{t.aiBody}</p>
              <div style={{ marginTop: 28 }}>
                <Link href={aiHref} className="btn-blue" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.aiCta}<Icon name="arrow-forward" size={18} /></Link>
              </div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid #2C3B35", borderRadius: 18, padding: 18, backdropFilter: "blur(4px)" }}>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                <span style={{ fontSize: 13, color: "var(--text-on-hero)", background: "var(--brand)", borderRadius: "14px 14px 4px 14px", padding: "10px 14px", maxWidth: "80%" }}>{t.aiQuestion}</span>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <span style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg,#FEC935,#FBA932)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name="sparkles" size={15} color="#fff" /></span>
                <div style={{ background: "#151D1A", border: "1px solid #2C3B35", borderRadius: "4px 14px 14px 14px", padding: 14, fontSize: 13, color: "var(--text-on-hero)", lineHeight: 1.55 }}>
                  <div style={{ marginBottom: 10 }}>{t.aiAnswerIntro}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "rgba(43,123,90,0.18)", borderRadius: 8, marginBottom: 6 }}>
                    <span style={{ flex: 1 }}>{t.aiRow1}</span>
                    <span style={{ fontFamily: "var(--font-mono)", color: "#86D6B0" }}>{t.aiRow1Val}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "rgba(165,116,35,0.16)", borderRadius: 8 }}>
                    <span style={{ flex: 1 }}>{t.aiRow2}</span>
                    <span style={{ fontFamily: "var(--font-mono)", color: "#E3B85F" }}>{t.aiRow2Val}</span>
                  </div>
                  <div style={{ marginTop: 10, color: "var(--text-hero-muted)" }}>{t.aiAnswerOutro}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURE GRID ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "48px 32px" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)", textWrap: "balance" }}>{t.gridH2}</h2>
        </div>
        <div className="ds-3col" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }}>
          {t.grid.map((f) => <FeatureCard key={f.title} {...f} />)}
        </div>
      </section>

      {/* ===== PLATFORM ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "48px 32px" }}>
        <div style={{ textAlign: "center", maxWidth: "52ch", margin: "0 auto 36px" }}>
          <div style={{ ...monoLabel, marginBottom: 14 }}>{t.platLabel}</div>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{t.platH2}</h2>
          <p style={{ fontSize: 17, color: "var(--text-muted)", margin: "16px auto 0" }}>{t.platSub}</p>
        </div>
        <div className="ds-3col" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
          {t.platform.map((p) => (
            <Link key={p.title} href={p.href} className="card-link" style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 14, padding: 16, textDecoration: "none", color: "inherit" }}>
              <span style={{ width: 38, height: 38, borderRadius: 10, background: p.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name={p.icon} size={19} color={p.iconColor} /></span>
              <span>
                <span style={{ display: "block", fontSize: 14, fontWeight: 600 }}>{p.title}</span>
                <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)" }}>{p.sub}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== SECURITY ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "24px 32px 8px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "18px 40px", padding: "28px 24px", background: "var(--green-50)", border: "1px solid var(--border)", borderRadius: 18 }}>
          <span style={{ ...monoLabel, color: "var(--text-muted)" }}>{t.secLabel}</span>
          {t.security.map((s) => (
            <span key={s.text} style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, color: "var(--green-900)" }}><Icon name={s.icon} size={18} color="var(--brand)" />{s.text}</span>
          ))}
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section style={{ maxWidth: 780, margin: "0 auto", padding: "64px 32px" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{t.faqH2}</h2>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {t.faqs.map((f) => <FaqItem key={f.q} q={f.q} a={f.a} />)}
        </div>
      </section>

      {/* ===== CTA BAND ===== */}
      <section style={{ padding: "0 32px 72px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", background: "var(--green-900)", borderRadius: 28, padding: "clamp(40px,6vw,72px) 32px", textAlign: "center", position: "relative", overflow: "hidden", boxShadow: "0 24px 60px rgba(16,33,27,0.22)" }}>
          <div aria-hidden style={{ position: "absolute", bottom: "-40%", left: "50%", transform: "translateX(-50%)", width: 600, height: 400, background: "radial-gradient(circle, rgba(249,115,22,0.2), rgba(249,115,22,0) 70%)", pointerEvents: "none" }} />
          <img src="/svendetid-logo.png" alt="" style={{ width: 48, height: 48, margin: "0 auto 22px", display: "block", position: "relative" }} />
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 44, lineHeight: 1.08, letterSpacing: "-0.02em", margin: "0 auto", maxWidth: "18ch", color: "#fff", position: "relative", textWrap: "balance" }}>{t.bandH2}</h2>
          <p style={{ fontSize: 17, color: "var(--text-hero-muted)", margin: "18px auto 0", maxWidth: "46ch", position: "relative" }}>{t.bandBody}</p>
          <div className="ds-cta-row" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginTop: 30, position: "relative" }}>
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-blue" style={{ cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 26px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.cta}<Icon name="arrow-forward" size={18} /></button></SignUpButton>
            <Link href={pricingHref} className="btn-ghost-dark" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, border: "1px solid rgba(255,255,255,0.24)" }}>{t.seePricing}</Link>
          </div>
        </div>
      </section>

      <Footer lang={lang} daHref="/features/calendar" enHref="/en/features/calendar" />
    </div>
  );
}
